import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash2, ArrowLeft, ImageIcon, Loader2, CheckCircle2, XCircle, Clock, RefreshCw, Tag } from "lucide-react";
import { optimizeImageForUpload, MAX_SOURCE_BYTES, ImageTooLargeError } from "./imageOptimizer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
// Source-file size cap is enforced by `optimizeImageForUpload` (10 MB). We
// re-export it here so the dropzone messaging shows the right number.
const MAX_BYTES = MAX_SOURCE_BYTES;

const humanBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const STATUS_PILL = {
  // Solid backgrounds with white text — readable regardless of theme.
  ready: { Icon: CheckCircle2, klass: "text-white bg-emerald-600 border-emerald-700", label: "Ready" },
  pending: { Icon: Clock, klass: "text-white bg-amber-600 border-amber-700", label: "Pending" },
  analyzing: { Icon: Loader2, klass: "text-white bg-sky-600 border-sky-700 animate-pulse", label: "Analyzing" },
  failed: { Icon: XCircle, klass: "text-white bg-rose-600 border-rose-700", label: "Failed" },
  unknown: { Icon: Clock, klass: "text-white bg-slate-500 border-slate-600", label: "Unknown" },
};

/**
 * StorageQuotaBanner — shows the user's tier storage usage as a horizontal
 * progress bar with an amber state at 80%+ and a rose/full state at 100%.
 * The "Upgrade plan" CTA only renders for non-pro tiers.
 */
const StorageQuotaBanner = ({ quota }) => {
  const pct = Math.min(1, quota.percent_used || 0);
  const isWarning = quota.status === "warning";
  const isFull = quota.status === "full";
  const barColor = isFull ? "bg-rose-600" : isWarning ? "bg-amber-500" : "bg-primary";
  const tierLabel = (() => {
    if (quota.tier === "pro_mentor") return "Pro Mentor";
    if (quota.tier === "creator") return "Creator";
    if (quota.tier === "solo") return "Solo";
    if (quota.tier === "trial") return "Trial";
    return quota.tier;
  })();
  return (
    <div
      className={`mb-6 border rounded-md p-4 ${
        isFull ? "border-rose-300 bg-rose-50/40" :
        isWarning ? "border-amber-300 bg-amber-50/40" :
        "border-border bg-card"
      }`}
      data-testid="library-storage-banner"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Storage on the {tierLabel} plan
        </div>
        <div className="text-xs text-muted-foreground" data-testid="library-storage-figures">
          <span className="text-foreground font-medium">{humanBytes(quota.bytes_used)}</span>
          {" of "}
          {humanBytes(quota.total_quota)}
          {quota.complimentary_extra > 0 && (
            <span className="ml-1 opacity-60">
              (incl. {humanBytes(quota.complimentary_extra)} complimentary)
            </span>
          )}
        </div>
      </div>
      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${(pct * 100).toFixed(1)}%` }}
          data-testid="library-storage-bar"
        />
      </div>
      {isWarning && (
        <div className="mt-3 text-xs text-amber-800" data-testid="library-storage-warning">
          You're approaching your storage limit. Consider deleting unused files or upgrading your plan.
        </div>
      )}
      {isFull && (
        <div className="mt-3 text-xs text-rose-800 flex items-center justify-between flex-wrap gap-2" data-testid="library-storage-full">
          <span>Storage full. Delete some files below or upgrade your plan to upload more.</span>
          {quota.tier !== "pro_mentor" && (
            <a
              href="/account"
              className="px-3 py-1.5 border border-rose-400 text-rose-800 rounded-sm uppercase tracking-widest text-[10px] hover:bg-rose-100 transition"
              data-testid="library-storage-upgrade-cta"
            >
              Upgrade plan
            </a>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * MyLibrary — manage personal inspiration AND wardrobe uploads per genre.
 *
 * Two top-level tabs:
 *  • Inspiration — drops images into `users/{userId}/` of each genre's inspo bucket.
 *  • Wardrobe — drops garments into `users/{userId}/{genre}/` of the shared wardrobe
 *    bucket; each upload queues a background GPT-Vision analysis (status pill on each tile).
 */
const Library = () => {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null); // null = loading, false = locked, true = unlocked
  const [genres, setGenres] = useState([]);
  const [activeKind, setActiveKind] = useState("inspiration"); // "inspiration" | "wardrobe"
  const [activeGenre, setActiveGenre] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-tab inventory: { inspiration: {counts:{},bytes:{}, total_bytes:0}, wardrobe: {genres:{}} }
  const [inspoInv, setInspoInv] = useState({ counts: {}, bytes: {}, total_bytes: 0 });
  const [wardrobeInv, setWardrobeInv] = useState({ genres: {} });
  // Storage quota — drives the per-tier headroom bar and pre-upload guard.
  const [quota, setQuota] = useState(null);
  const [uploading, setUploading] = useState({ done: 0, total: 0 });
  // Per-batch upload gender (only used when the active genre has gender_folders).
  // Defaults to the active genre's `ambiguous_prefix` so users who skip the
  // picker still get a sensible bucket location.
  const [uploadGender, setUploadGender] = useState("");
  const pollTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [fs, gs] = await Promise.all([
          axios.get(`${API}/uploads/feature-status`, { withCredentials: true }),
          axios.get(`${API}/genres`, { withCredentials: true }),
        ]);
        setEnabled(!!fs.data?.enabled);
        const list = (gs.data?.genres || []).filter((g) => g.has_pack && g.has_inspo_bucket);
        setGenres(list);
        if (list.length && !activeGenre) setActiveGenre(list[0].key);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load library");
        setEnabled(false);
      }
    })();
  }, []);

  const refreshInventory = useCallback(async () => {
    if (!enabled) return;
    try {
      const [inspo, wd, q] = await Promise.all([
        axios.get(`${API}/uploads/inventory`, { withCredentials: true }),
        axios.get(`${API}/wardrobe/inventory`, { withCredentials: true }),
        axios.get(`${API}/storage/quota`, { withCredentials: true }),
      ]);
      setInspoInv(inspo.data || { counts: {}, bytes: {}, total_bytes: 0 });
      setWardrobeInv(wd.data || { genres: {} });
      setQuota(q.data || null);
    } catch (e) {
      // Silent — inventory failures should not block the page
    }
  }, [enabled]);

  // Detailed pack for the active genre (gives us gender_folders for the picker).
  const [activePack, setActivePack] = useState(null);
  useEffect(() => {
    if (!activeGenre) { setActivePack(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/genres/${activeGenre}`, { withCredentials: true });
        setActivePack(r.data);
      } catch (e) {
        setActivePack(null);
      }
    })();
  }, [activeGenre]);

  // Reset upload gender to the genre's ambiguous default whenever the active
  // genre (or kind) changes. Wardrobe never uses gender folders today, so we
  // only need this for inspiration uploads.
  useEffect(() => {
    const cfg = activePack?.gender_folders;
    if (activeKind === "inspiration" && cfg?.enabled) {
      setUploadGender(cfg.ambiguous_prefix || "ambiguous");
    } else {
      setUploadGender("");
    }
  }, [activePack, activeKind]);

  // Load items for active tab+genre
  const refreshItems = useCallback(async () => {
    if (!enabled || !activeGenre) return;
    setLoading(true);
    try {
      const endpoint = activeKind === "wardrobe" ? "wardrobe" : "uploads";
      const r = await axios.get(`${API}/${endpoint}`, { params: { genre: activeGenre }, withCredentials: true });
      setItems(r.data?.images || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load images");
    } finally {
      setLoading(false);
    }
  }, [enabled, activeGenre, activeKind]);

  useEffect(() => { refreshItems(); refreshInventory(); }, [refreshItems, refreshInventory]);

  // Lightweight status-only refresh used by the wardrobe poller. Merges new
  // status/garment fields into existing items by key without touching the
  // signed URL — prevents the grid from flashing on every poll tick.
  const refreshWardrobeStatuses = useCallback(async () => {
    if (activeKind !== "wardrobe" || !activeGenre) return;
    try {
      const r = await axios.get(`${API}/wardrobe/status`, {
        params: { genre: activeGenre },
        withCredentials: true,
      });
      const byKey = {};
      (r.data?.items || []).forEach((it) => { byKey[it.image_key] = it; });
      setItems((prev) => prev.map((img) => {
        const u = byKey[img.key];
        if (!u) return img;
        return {
          ...img,
          status: u.status || img.status,
          garment_type: u.garment_type ?? img.garment_type,
          color: u.color ?? img.color,
          style: u.style ?? img.style,
          comfort_level: u.comfort_level ?? img.comfort_level,
        };
      }));
    } catch (e) {
      // Silent — polling shouldn't toast errors
    }
  }, [activeKind, activeGenre]);

  // Auto-poll wardrobe status while any item is pending/analyzing
  useEffect(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (activeKind !== "wardrobe") return;
    const hasInflight = items.some((i) => i.status === "pending" || i.status === "analyzing");
    if (!hasInflight) return;
    pollTimer.current = setInterval(() => { refreshWardrobeStatuses(); refreshInventory(); }, 3000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [activeKind, items, refreshWardrobeStatuses, refreshInventory]);

  const onFiles = async (fileList) => {
    if (!enabled || !activeGenre) return;
    const files = Array.from(fileList).filter((f) => {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        toast.error(`Skipped ${f.name}: unsupported type ${f.type || "?"}`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`Skipped ${f.name}: exceeds 10 MB (file is ${(f.size / (1024 * 1024)).toFixed(1)} MB)`);
        return false;
      }
      return true;
    });
    if (!files.length) return;

    setUploading({ done: 0, total: files.length });
    const presignPath = activeKind === "wardrobe" ? "/wardrobe/presign" : "/uploads/presign";
    const succeededKeys = [];
    let succeeded = 0;
    let attempted = 0;

    for (const f of files) {
      attempted += 1;
      try {
        // Optimize BEFORE the presign so the backend gets the correct
        // content_type + size for the actual bytes we're about to upload.
        // The original file is never sent — we only PUT the processed JPEG.
        let processed;
        try {
          processed = await optimizeImageForUpload(f);
        } catch (optErr) {
          if (optErr instanceof ImageTooLargeError) {
            toast.error(optErr.message);
          } else {
            toast.error(`Could not process ${f.name}: ${optErr.message || "decode failed"}`);
          }
          continue;
        }

        // Only send `gender` for inspiration uploads — wardrobe routes through
        // its own endpoint which has its own genre-scoped key path.
        const presignBody = {
          genre: activeGenre,
          filename: processed.name,
          content_type: processed.type,
          size: processed.size,
        };
        if (activeKind === "inspiration" && uploadGender) {
          presignBody.gender = uploadGender;
        }
        const presign = await axios.post(
          `${API}${presignPath}`,
          presignBody,
          { withCredentials: true }
        );
        const { upload_url, content_type, key } = presign.data;
        await axios.put(upload_url, processed, {
          headers: { "Content-Type": content_type },
          withCredentials: false,
        });
        succeededKeys.push(key);
        succeeded += 1;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Upload failed";
        // Server returns 402 with a structured detail when quota is exceeded.
        // Surface a clear CTA and stop the batch — further uploads will all fail.
        if (e.response?.status === 402) {
          const d = e.response.data?.detail || {};
          toast.error(
            d.message || "Storage quota exceeded — upgrade your plan or delete unused files."
          );
          // Skip remaining files in this batch.
          break;
        }
        const looksLikeCors = !e.response && /network|cors/i.test(msg);
        toast.error(
          looksLikeCors
            ? `${f.name}: blocked by S3 CORS. Verify the bucket's CORS policy allows PUT from this origin.`
            : `Failed to upload ${f.name}: ${msg}`
        );
      } finally {
        setUploading((u) => ({ ...u, done: attempted }));
      }
    }

    // Tell the backend about successful PUTs so it can (a) enqueue background
    // AI analysis for wardrobe and (b) record the actual stored size against
    // the user's storage quota. Both endpoints are best-effort and idempotent.
    if (succeededKeys.length) {
      const finalizePath = activeKind === "wardrobe" ? "/wardrobe/uploaded" : "/uploads/uploaded";
      try {
        await axios.post(
          `${API}${finalizePath}`,
          { genre: activeGenre, keys: succeededKeys },
          { withCredentials: true }
        );
      } catch (e) {
        if (activeKind === "wardrobe") {
          toast.error("Uploaded, but failed to queue analysis — try Retry on each item");
        }
        // Silent for inspiration — quota counter will reconcile on next admin sweep.
      }
    }

    if (succeeded === files.length) {
      toast.success(`Uploaded ${succeeded} of ${files.length} file${files.length === 1 ? "" : "s"}`);
    } else if (succeeded > 0) {
      toast.warning(`Uploaded ${succeeded} of ${files.length} — see errors above`);
    }
    setUploading({ done: 0, total: 0 });
    refreshItems();
    refreshInventory();
  };

  const onDelete = async (image) => {
    if (!confirm("Delete this image? This cannot be undone.")) return;
    const endpoint = activeKind === "wardrobe" ? "wardrobe" : "uploads";
    try {
      await axios.delete(`${API}/${endpoint}`, { params: { genre: activeGenre, key: image.key }, withCredentials: true });
      toast.success("Deleted");
      setItems((prev) => prev.filter((i) => i.key !== image.key));
      refreshInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onRetry = async (image) => {
    try {
      await axios.post(
        `${API}/wardrobe/retry-analysis`,
        { genre: activeGenre, keys: [image.key] },
        { withCredentials: true }
      );
      toast.info("Re-queued analysis");
      refreshItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  // Which item is currently showing its re-tag popover (or null).
  const [retagKey, setRetagKey] = useState(null);

  // Close the re-tag popover on any outside click or Escape key.
  useEffect(() => {
    if (!retagKey) return;
    const dismiss = () => setRetagKey(null);
    const onKey = (e) => { if (e.key === "Escape") setRetagKey(null); };
    document.addEventListener("click", dismiss);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", dismiss);
      document.removeEventListener("keydown", onKey);
    };
  }, [retagKey]);

  const onRetag = async (image, newGender) => {
    setRetagKey(null);
    if (!newGender || image.gender === newGender) return;
    try {
      const r = await axios.post(
        `${API}/uploads/retag`,
        { genre: activeGenre, key: image.key, new_gender: newGender },
        { withCredentials: true }
      );
      const { new_key, url } = r.data || {};
      // Optimistically swap the item in place so the badge updates without
      // a full grid reflow (which would re-fetch every signed URL).
      setItems((prev) => prev.map((i) =>
        i.key === image.key
          ? { ...i, key: new_key || i.key, url: url || i.url, gender: newGender }
          : i
      ));
      refreshInventory();
      toast.success("Image re-tagged");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-tag failed");
    }
  };

  if (enabled === null) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (enabled === false) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <h1 className="font-heading text-4xl mb-4">My Library</h1>
        <p className="text-muted-foreground mb-6">
          The image-upload feature isn't enabled on your account yet. Reach out to your administrator to unlock per-genre image uploads.
        </p>
        <button
          onClick={() => navigate("/")}
          className="text-xs uppercase tracking-widest text-primary hover:underline"
          data-testid="library-back-locked"
        >
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  // Derived: total usage indicator string for the current tab
  let usageLabel = "";
  if (activeKind === "inspiration") {
    usageLabel = humanBytes(inspoInv.total_bytes || 0);
  } else {
    const totalReady = Object.values(wardrobeInv.genres || {}).reduce((s, g) => s + (g.ready || 0), 0);
    const totalAll = Object.values(wardrobeInv.genres || {}).reduce((s, g) => s + (g.total || 0), 0);
    usageLabel = totalAll > 0 ? `${totalReady}/${totalAll} ready` : "No items";
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="library-page">
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-6"
        data-testid="library-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl tracking-tight">My Library</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {activeKind === "wardrobe"
              ? "Upload garment images per genre. Each upload runs a quick AI analysis so your wardrobe can be matched to Portrait Plan themes."
              : "Upload your own inspiration images per genre. They'll be mixed with the curated library when you analyze a session."}
          </p>
        </div>
        <div className="text-right text-xs text-muted-foreground" data-testid="library-usage">
          <div>{activeKind === "wardrobe" ? "Garments analyzed" : "Total storage"}</div>
          <div className="text-lg text-foreground font-medium">{usageLabel}</div>
        </div>
      </div>

      {/* Top-level kind tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: "inspiration", label: "Inspiration" },
          { key: "wardrobe", label: "Wardrobe" },
        ].map((t) => {
          const sel = activeKind === t.key;
          return (
            <button
              key={t.key}
              onClick={() => { setActiveKind(t.key); setItems([]); }}
              className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm transition ${
                sel ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
              data-testid={`library-kind-tab-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Genre sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-2">
        {genres.map((g) => {
          const sel = activeGenre === g.key;
          const count = activeKind === "wardrobe"
            ? (wardrobeInv.genres?.[g.key]?.total || 0)
            : (inspoInv.counts?.[g.key] || 0);
          return (
            <button
              key={g.key}
              onClick={() => setActiveGenre(g.key)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 transition ${
                sel ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`library-genre-tab-${g.key}`}
            >
              {g.name} <span className="ml-1 opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Per-batch gender picker — only for inspiration uploads to a gendered genre. */}
      {activeKind === "inspiration" && activePack?.gender_folders?.enabled && (
        <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="library-gender-picker">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Tag this upload batch as
          </span>
          {[
            ...(activePack.gender_folders.options || []),
            {
              value: activePack.gender_folders.ambiguous_prefix || "ambiguous",
              label: "Either",
              prefix: activePack.gender_folders.ambiguous_prefix || "ambiguous",
            },
          ].map((opt) => {
            const sel = uploadGender === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setUploadGender(opt.value)}
                className={`px-3 py-1.5 border rounded-sm text-xs uppercase tracking-widest transition ${
                  sel
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/40"
                }`}
                data-testid={`library-gender-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Storage quota banner — shows headroom and gates uploads at 100%.
          Admins (status=exempt) and users without upload access (status=no_access)
          don't see a bar. */}
      {quota && quota.status !== "exempt" && quota.status !== "no_access" && (
        <StorageQuotaBanner quota={quota} />
      )}

      {/* Drag-and-drop area */}
      <label
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (quota?.status === "full") {
            toast.error("Storage full — delete unused files or upgrade your plan to upload more.");
            return;
          }
          onFiles(e.dataTransfer.files);
        }}
        className={`block border-2 border-dashed rounded-md p-10 text-center mb-8 transition ${
          quota?.status === "full"
            ? "border-rose-300 bg-rose-50/40 cursor-not-allowed opacity-60"
            : "border-border cursor-pointer hover:border-primary/60"
        }`}
        data-testid="library-dropzone"
      >
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            if (quota?.status === "full") {
              toast.error("Storage full — delete unused files or upgrade your plan to upload more.");
              e.target.value = "";
              return;
            }
            onFiles(e.target.files);
          }}
          disabled={quota?.status === "full"}
          className="hidden"
          data-testid="library-file-input"
        />
        <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
        <div className="text-sm text-foreground mb-1">
          {quota?.status === "full"
            ? "Storage full — delete files or upgrade to upload more"
            : "Drop images here, or click to browse"}
        </div>
        <div className="text-xs text-muted-foreground">JPG · PNG · WEBP · up to 10 MB each · auto-optimized to JPEG at 2000 px</div>
        {uploading.total > 0 && (
          <div className="mt-4 text-xs text-primary" data-testid="library-upload-progress">
            Uploading {uploading.done}/{uploading.total}…
          </div>
        )}
      </label>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="library-empty">
          <ImageIcon className="w-10 h-10 mx-auto opacity-30 mb-3" />
          No {activeKind === "wardrobe" ? "garments" : "images"} uploaded for this genre yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="library-grid">
          {items.map((img) => {
            const status = img.status || (activeKind === "wardrobe" ? "unknown" : "ready");
            const pill = STATUS_PILL[status] || STATUS_PILL.unknown;
            // Resolve a human-readable gender label for inspiration items in
            // gendered genres. Falls back to the raw prefix if no label match.
            const genderLabel = (() => {
              if (activeKind !== "inspiration" || !img.gender) return null;
              const opts = activePack?.gender_folders?.options || [];
              const match = opts.find((o) => (o.prefix || o.value) === img.gender);
              if (match) return match.label;
              const amb = activePack?.gender_folders?.ambiguous_prefix || "ambiguous";
              return img.gender === amb ? "Either" : img.gender;
            })();
            return (
              <div
                key={img.key}
                className="relative group aspect-square rounded-sm overflow-hidden border border-border"
                data-testid={`library-image-${img.key.split("/").pop()}`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />

                {/* Gender badge (inspiration only, gendered genres only) */}
                {genderLabel && (
                  <div
                    className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-black/70 text-white border border-white/10 rounded-full"
                    data-testid={`library-gender-badge-${img.gender}`}
                  >
                    {genderLabel}
                  </div>
                )}

                {/* Re-tag affordance — inspiration uploads on gendered genres only.
                    Shows the existing options (e.g. Boy / Girl / Either) minus the
                    current value, so a click is always a meaningful change. */}
                {activeKind === "inspiration" && activePack?.gender_folders?.enabled && img.gender && (
                  <div className="absolute top-2 right-12">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRetagKey(retagKey === img.key ? null : img.key);
                      }}
                      className="bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                      data-testid={`library-retag-${img.key.split("/").pop()}`}
                      title="Re-tag gender"
                    >
                      <Tag className="w-3.5 h-3.5" />
                    </button>
                    {retagKey === img.key && (
                      <div
                        className="absolute right-0 top-7 z-10 bg-card border border-border rounded-sm shadow-lg overflow-hidden min-w-[110px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {[
                          ...(activePack.gender_folders.options || []),
                          {
                            value: activePack.gender_folders.ambiguous_prefix || "ambiguous",
                            label: "Either",
                            prefix: activePack.gender_folders.ambiguous_prefix || "ambiguous",
                          },
                        ]
                          .filter((opt) => (opt.prefix || opt.value) !== img.gender)
                          .map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => onRetag(img, opt.prefix || opt.value)}
                              className="block w-full text-left px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-primary/10 hover:text-primary transition"
                              data-testid={`library-retag-option-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Status pill (wardrobe only) */}
                {activeKind === "wardrobe" && (
                  <div
                    className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-full ${pill.klass}`}
                    data-testid={`library-status-${status}`}
                  >
                    <pill.Icon className="w-3 h-3" />
                    {pill.label}
                  </div>
                )}

                {/* Retry button when failed */}
                {activeKind === "wardrobe" && status === "failed" && (
                  <button
                    onClick={() => onRetry(img)}
                    className="absolute top-2 right-12 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                    title="Retry analysis"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}

                <button
                  onClick={() => onDelete(img)}
                  className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`library-delete-${img.key.split("/").pop()}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100">
                  {activeKind === "wardrobe" && img.garment_type
                    ? `${img.garment_type} · ${img.color || ""}`.trim()
                    : humanBytes(img.size)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Library;
